import { useState, useEffect, useCallback } from 'react';
import Toast, { useToast } from '../components/Toast';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ClientTrail, { tabCrumbs, clientTabPath } from '../components/ClientTrail';
import { supabase, createSupervisorVisitVersion } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useFormDraftBackup } from '../hooks/useFormDraftBackup';
import { downloadSupervisorVisitDoc, generateSupervisorVisitDoc } from '../lib/generateSupervisorVisitDoc';
import { uploadAndLinkDoc } from '../lib/driveDocs';
import { format, addDays, parseISO, differenceInDays, addBusinessDays, differenceInBusinessDays } from 'date-fns';
import { getNonGappCadence, CADENCE } from '../lib/supervisoryCadence';

// Physician summary target — conservative end of the 60-90 day HFRD window,
// independent of the visit cadence above (see Policy A-3.4).
const PHYSICIAN_SUMMARY_TARGET_DAYS = 60;

const VISIT_TYPE_OPTIONS_GAPP = [
  { value: 'initial_visit',        label: 'Initial visit (new client, within 30 days)' },
  { value: 'gapp_pss_biweekly',    label: 'GAPP — PSS/family caregiver (biweekly)' },
  { value: 'gapp_rn_monthly',      label: 'GAPP — RN assessment of LPN-delivered nursing (monthly)' },
  { value: 'unannounced',          label: 'Unannounced' },
  { value: 'follow_up',            label: 'Follow-up' },
];

const VISIT_TYPE_OPTIONS_NON_GAPP = [
  { value: 'initial_visit', label: 'Initial visit (new client, within 30 days)' },
  { value: 'scheduled',     label: 'Scheduled' },
  { value: 'unannounced',   label: 'Unannounced' },
  { value: 'follow_up',     label: 'Follow-up' },
];

const VISIT_TYPE_LABELS = {
  initial_visit:     'Initial visit',
  gapp_pss_biweekly: 'GAPP — PSS/family caregiver',
  gapp_rn_monthly:   'GAPP — RN monthly assessment',
  scheduled:         'Scheduled',
  unannounced:       'Unannounced',
  follow_up:         'Follow-up',
};

// ── Constants ─────────────────────────────────────────────────

const TABS = [
  'Visit details',
  'Clinical observation',
  'Nurse performance',
  'Family feedback',
  'Physician summary',
  'Review & complete',
];

const COMPETENCIES = [
  'Tracheostomy care',
  'G-tube management',
  'Ventilator management',
  'Emergency protocols',
  'Medication administration',
  'Documentation standards',
];

const NAVY  = 'var(--accent)';
const STEEL = 'var(--accent)';
const BORDER = 'var(--border2)';
const LIGHT  = 'var(--bg2)';

// ── Module-scope sub-components ───────────────────────────────

function TabButton({ label, index, active, onClick }) {
  return (
    <button onClick={() => onClick(index)} style={{
      padding: '9px 16px', fontSize: 12, fontWeight: active ? 600 : 400,
      color: active ? '#fff' : 'var(--text2)',
      background: active ? NAVY : 'transparent',
      border: 'none', borderBottom: active ? `2px solid ${STEEL}` : '2px solid transparent',
      cursor: 'pointer', whiteSpace: 'nowrap', borderRadius: active ? '6px 6px 0 0' : 0,
      transition: 'all 0.12s',
    }}>
      <span style={{ marginRight: 6, opacity: 0.7 }}>{index + 1}.</span>{label}
    </button>
  );
}

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            fontSize: 24, color: n <= (hover || value || 0) ? 'var(--warn)' : 'var(--border2)',
            transition: 'color 0.1s' }}>
          ★
        </button>
      ))}
      {value ? <span style={{ fontSize: 12, color: 'var(--text3)', alignSelf: 'center', marginLeft: 4 }}>{value}/5</span> : null}
    </div>
  );
}

function ChecklistItem({ label, subLabel, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0',
      borderBottom: `1px solid ${BORDER}`, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)}
        style={{ marginTop: 2, accentColor: STEEL, width: 16, height: 16, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{label}</div>
        {subLabel && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{subLabel}</div>}
      </div>
    </label>
  );
}

function FieldLabel({ children, required }) {
  return (
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: NAVY,
      textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>
      {children}{required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
    </label>
  );
}

function FInput({ value, onChange, type = 'text', placeholder, readOnly }) {
  return (
    <input type={type} value={value || ''} placeholder={placeholder || ''}
      readOnly={readOnly} onChange={e => !readOnly && onChange(e.target.value)}
      style={{ width: '100%', padding: '8px 11px', borderRadius: 6, fontSize: 13,
        border: `1.5px solid ${BORDER}`, background: readOnly ? 'var(--bg2)' : '#fff',
        color: 'var(--text)', outline: 'none', boxSizing: 'border-box',
        cursor: readOnly ? 'not-allowed' : 'text' }} />
  );
}

function FSelect({ value, onChange, options }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      style={{ width: '100%', padding: '8px 11px', borderRadius: 6, fontSize: 13,
        border: `1.5px solid ${BORDER}`, background: '#fff', color: 'var(--text)',
        outline: 'none', boxSizing: 'border-box', cursor: 'pointer' }}>
      <option value="">— Select —</option>
      {options.map(o => (
        <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
      ))}
    </select>
  );
}

function FTextarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder || ''} rows={rows}
      style={{ width: '100%', padding: '8px 11px', borderRadius: 6, fontSize: 13,
        border: `1.5px solid ${BORDER}`, background: '#fff', color: 'var(--text)',
        outline: 'none', boxSizing: 'border-box', resize: 'vertical',
        fontFamily: 'inherit' }} />
  );
}

function FieldRow({ children }) {
  return <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>{children}</div>;
}

function Field({ label, required, children, half, third }) {
  return (
    <div style={{ flex: third ? '1 1 30%' : half ? '1 1 45%' : '1 1 100%', minWidth: 180 }}>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      {children}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={{ background: 'var(--card-bg)', border: `1px solid ${BORDER}`,
      borderRadius: 10, marginBottom: 20, overflow: 'hidden' }}>
      <div style={{ background: LIGHT, padding: '11px 18px', borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: NAVY,
          textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</div>
      </div>
      <div style={{ padding: '18px 18px 6px' }}>{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      padding: '7px 0', borderBottom: `1px solid ${BORDER}`, gap: 12 }}>
      <span style={{ fontSize: 12, color: 'var(--text3)', flexShrink: 0, minWidth: 180 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, textAlign: 'right' }}>{value || '—'}</span>
    </div>
  );
}


// ── Empty form state ──────────────────────────────────────────

const EMPTY = {
  supervisor_name: '', supervisor_id: '',
  visit_date: format(new Date(), 'yyyy-MM-dd'),
  visit_type: '', nurse_present: null,
  assigned_nurse_name: '', visit_duration_minutes: '',

  overall_condition: '', vital_signs_status: '', skin_integrity: '',
  equipment_functioning: '', clinical_notes: '', medication_technique: '',
  medication_list_current: '',

  documentation_current: false, care_plan_followed: false,
  infection_control_observed: false, emergency_protocol_reviewed: false,
  family_interaction_ok: false, caregiver_education_ongoing: false,
  performance_rating: '', strengths: '', improvements_needed: '',
  competency_followup: [], followup_visit_required: false, followup_visit_date: '',
  corrective_action_required: false, corrective_action_due_date: '',
  corrective_action_resolved_at: null, corrective_action_resolved_by: '',

  satisfaction_overall: 0, satisfaction_consistency: 0,
  satisfaction_communication: 0, satisfaction_care_plan: 0, family_concerns: '',

  physician_name: '', physician_delivery_method: '', physician_fax_email: '',
  summary_period_start: '', summary_period_end: '',
  ai_summary_draft: '', final_summary: '', recommended_physician_actions: '',

  lpn_performed_on_rn_behalf: false, rn_countersigner_name: '',
  rn_countersignature_due_date: '', rn_countersigned_at: null, rn_countersigned_by: '',
  sentinel_event: false, sentinel_event_date: '',
};

// ── Main component ────────────────────────────────────────────

export default function SupervisorVisitForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');
  const navigate = useNavigate();
  const { profile } = useAuth();

  const isNew = !id || id === 'new';

  const [activeTab,    setActiveTab]    = useState(0);
  const [formData,     setFormData]     = useState(EMPTY);
  const [client,       setClient]       = useState(null);
  const [supervisors,  setSupervisors]  = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [generating,   setGenerating]   = useState(false);
  const [attested,     setAttested]     = useState(false);
  const { toast, showToast, hideToast } = useToast(4000);
  const [visitId,      setVisitId]      = useState(id !== 'new' ? id : null);
  const [loadedStatus, setLoadedStatus] = useState('draft'); // status at load time
  const [ready,        setReady]        = useState(false);   // true after a clean load
  const [loadError,    setLoadError]    = useState('');      // set if the saved visit fails to load
  // Physician summary cadence is independent of the visit cadence (Policy
  // A-3.4, 60-90 days, HFRD-universal) — not tied to this specific visit.
  const [lastSummaryDate, setLastSummaryDate] = useState(null);
  const [summaryDue,      setSummaryDue]      = useState(true); // conservative default until resolved

  // Device backup net: mirrors the form to localStorage so work survives session
  // expiry / lost connectivity / crashes; restored on reopen if newer than server.
  const draftKey = `sv_form_${isNew ? (clientId || 'new') : id}`;
  const { readBackup, clearBackup } = useFormDraftBackup({ storageKey: draftKey, data: formData, enabled: ready });


  const set = useCallback((key) => (val) => {
    setFormData(prev => ({ ...prev, [key]: val }));
  }, []);

  // Load supervisor list
  useEffect(() => {
    supabase.from('profiles').select('id, full_name, role')
      .in('role', ['admin', 'manager', 'coordinator', 'nurse'])
      .order('full_name')
      .then(({ data }) => setSupervisors(data || []));
  }, []);

  // Load visit + client data
  useEffect(() => {
    const load = async () => {
      let cid = clientId;
      let serverUpdatedAt = 0;

      // Load existing visit
      if (!isNew && id) {
        const { data: v, error: vErr } = await supabase
          .from('supervisor_visits')
          .select('*')
          .eq('id', id)
          .maybeSingle();
        if (vErr) {
          // Read failed — block editing so a blank form can't overwrite the
          // saved visit. (maybeSingle: "no row" is data:null with no error.)
          console.error('Failed to load supervisor visit:', vErr);
          setLoadError('Could not load this visit. Please reload the page before editing to avoid overwriting saved data.');
          return;
        }
        if (v) {
          setVisitId(v.id);
          setLoadedStatus(v.status || 'draft');
          serverUpdatedAt = new Date(v.updated_at || 0).getTime();
          cid = cid || v.client_id;
          setFormData({
            supervisor_name: v.supervisor_name || '',
            supervisor_id: v.supervisor_id || '',
            visit_date: v.visit_date || format(new Date(), 'yyyy-MM-dd'),
            visit_type: v.visit_type || '',
            nurse_present: v.nurse_present,
            assigned_nurse_name: v.assigned_nurse_name || '',
            visit_duration_minutes: v.visit_duration_minutes || '',
            overall_condition: v.overall_condition || '',
            vital_signs_status: v.vital_signs_status || '',
            skin_integrity: v.skin_integrity || '',
            equipment_functioning: v.equipment_functioning || '',
            clinical_notes: v.clinical_notes || '',
            medication_technique: v.medication_technique || '',
            medication_list_current: v.medication_list_current || '',
            documentation_current: v.documentation_current || false,
            care_plan_followed: v.care_plan_followed || false,
            infection_control_observed: v.infection_control_observed || false,
            emergency_protocol_reviewed: v.emergency_protocol_reviewed || false,
            family_interaction_ok: v.family_interaction_ok || false,
            caregiver_education_ongoing: v.caregiver_education_ongoing || false,
            performance_rating: v.performance_rating || '',
            strengths: v.strengths || '',
            improvements_needed: v.improvements_needed || '',
            competency_followup: v.competency_followup || [],
            followup_visit_required: v.followup_visit_required || false,
            followup_visit_date: v.followup_visit_date || '',
            corrective_action_required: v.corrective_action_required || false,
            corrective_action_due_date: v.corrective_action_due_date || '',
            corrective_action_resolved_at: v.corrective_action_resolved_at || null,
            corrective_action_resolved_by: v.corrective_action_resolved_by || '',
            satisfaction_overall: v.satisfaction_overall || 0,
            satisfaction_consistency: v.satisfaction_consistency || 0,
            satisfaction_communication: v.satisfaction_communication || 0,
            satisfaction_care_plan: v.satisfaction_care_plan || 0,
            family_concerns: v.family_concerns || '',
            physician_name: v.physician_name || '',
            physician_delivery_method: v.physician_delivery_method || '',
            physician_fax_email: v.physician_fax_email || '',
            summary_period_start: v.summary_period_start || '',
            summary_period_end: v.summary_period_end || '',
            ai_summary_draft: v.ai_summary_draft || '',
            final_summary: v.final_summary || '',
            recommended_physician_actions: v.recommended_physician_actions || '',
            lpn_performed_on_rn_behalf: v.lpn_performed_on_rn_behalf || false,
            rn_countersigner_name: v.rn_countersigner_name || '',
            rn_countersignature_due_date: v.rn_countersignature_due_date || '',
            rn_countersigned_at: v.rn_countersigned_at || null,
            rn_countersigned_by: v.rn_countersigned_by || '',
            sentinel_event: v.sentinel_event || false,
            sentinel_event_date: v.sentinel_event_date || '',
          });
        }
      }

      // Load client for pre-fills
      if (cid) {
        const { data: c } = await supabase.from('clients').select('*').eq('id', cid).single();
        if (c) {
          setClient(c);
          setFormData(prev => ({
            ...prev,
            physician_name: prev.physician_name || c.doctor_name || '',
            physician_fax_email: prev.physician_fax_email || c.doctor_fax || '',
            summary_period_end: prev.summary_period_end || format(new Date(), 'yyyy-MM-dd'),
          }));
        }

        // Physician summary due-check: independent 60-90 day cadence, not
        // tied to this visit — find the most recently sent summary for
        // this client and see if the target interval has elapsed.
        const { data: lastSummary } = await supabase
          .from('physician_summaries')
          .select('period_end, sent_at')
          .eq('client_id', cid)
          .order('period_end', { ascending: false })
          .limit(1)
          .maybeSingle();
        const lastDate = lastSummary?.period_end || lastSummary?.sent_at || null;
        setLastSummaryDate(lastDate);
        setSummaryDue(!lastDate || differenceInDays(new Date(), parseISO(lastDate.slice(0, 10))) >= PHYSICIAN_SUMMARY_TARGET_DAYS);
      }

      // Offer to restore newer unsaved work from this device.
      const backup = readBackup();
      if (backup?.data) {
        const localTime = new Date(backup._savedAt || 0).getTime();
        if (localTime > serverUpdatedAt + 1000) {
          // eslint-disable-next-line no-alert
          if (window.confirm(`Unsaved changes from this device were found (last edited ${new Date(backup._savedAt).toLocaleString()}) that are newer than the saved copy. Restore them?`)) {
            setFormData(backup.data);
          } else {
            clearBackup();
          }
        }
      }

      // Enable the device-backup net only after a clean load.
      setTimeout(() => setReady(true), 400);
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, clientId, isNew]);

  // Given the visit_type just completed, returns the ISO date of its next
  // occurrence — or null for types that don't drive their own recurring
  // cadence (initial_visit / unannounced / follow_up; the regular cadence
  // track continues untouched for those).
  const nextDueDateFor = (visitType, fromDate) => {
    if (visitType === 'gapp_pss_biweekly') return format(addDays(fromDate, CADENCE.gapp_pss_biweekly.intervalDays), 'yyyy-MM-dd');
    if (visitType === 'gapp_rn_monthly')   return format(addDays(fromDate, CADENCE.gapp_rn_monthly.intervalDays), 'yyyy-MM-dd');
    if (visitType === 'scheduled')         return format(addDays(fromDate, getNonGappCadence(client).intervalDays), 'yyyy-MM-dd');
    return null;
  };

  // Build payload for save
  const buildPayload = (status = 'draft') => {
    const base = {
      client_id:   client?.id || null,
      client_ref:  client?.client_id,
      client_name: client ? `${client.first_name} ${client.last_name}` : formData.client_name,
      ...formData,
      visit_duration_minutes: formData.visit_duration_minutes ? parseInt(formData.visit_duration_minutes) : null,
      satisfaction_overall:     formData.satisfaction_overall     || null,
      satisfaction_consistency: formData.satisfaction_consistency || null,
      satisfaction_communication: formData.satisfaction_communication || null,
      satisfaction_care_plan:   formData.satisfaction_care_plan   || null,
      // Convert empty date strings to null for Postgres date columns
      visit_date: formData.visit_date || null,
      followup_visit_date: formData.followup_visit_date || null,
      summary_period_start: formData.summary_period_start || null,
      summary_period_end: formData.summary_period_end || null,
      sentinel_event_date: formData.sentinel_event_date || null,
      // System-computed due dates, not user-editable — recomputed here
      // rather than trusted from formData, same as next_visit_due below.
      corrective_action_due_date: formData.corrective_action_required && formData.visit_date
        ? format(addDays(parseISO(formData.visit_date), 14), 'yyyy-MM-dd') : null,
      rn_countersignature_due_date: formData.lpn_performed_on_rn_behalf && formData.visit_date
        ? format(addBusinessDays(parseISO(formData.visit_date), 10), 'yyyy-MM-dd') : null,
      // Convert empty UUID strings to null for Postgres UUID columns
      supervisor_id: formData.supervisor_id || null,
      status,
    };
    if (status === 'complete') {
      const vDate = formData.visit_date;
      const summarySent = summaryDue || !!formData.final_summary?.trim();
      base.completed_at   = new Date().toISOString();
      base.completed_by   = profile?.full_name;
      base.next_visit_due = nextDueDateFor(formData.visit_type, parseISO(vDate));
      base.summary_sent   = summarySent;
      base.summary_sent_at = summarySent ? new Date().toISOString() : null;
      base.summary_sent_by = summarySent ? profile?.full_name : null;
    }
    return base;
  };

  // Save draft
  const saveDraft = async () => {
    if (loadError) { showToast('Reload the page before saving — the saved visit did not load.', 'error'); return; }
    if (!formData.supervisor_name || !formData.visit_date || !formData.visit_type) {
      showToast('Please fill in Supervisor name, Visit date and Visit type before saving.', 'error');
      return;
    }
    setSaving(true);
    const wasComplete = loadedStatus === 'complete';
    try {
      const payload = buildPayload('draft');
      if (visitId) {
        const { error } = await supabase.from('supervisor_visits').update(payload).eq('id', visitId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('supervisor_visits').insert([payload]).select('id').single();
        if (error) throw error;
        setVisitId(data.id);
      }
      clearBackup(); // server now has it — drop the device backup
      if (wasComplete) {
        setLoadedStatus('draft');
        setAttested(false);
        showToast('Visit re-opened as Draft.');
      } else {
        showToast('Draft saved.');
      }
    } catch (e) {
      showToast(`Save failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Generate AI summary
  const generateSummary = async () => {
    if (!formData.summary_period_start || !formData.summary_period_end) {
      showToast('Please enter the Summary Period Start and End dates before generating.', 'error');
      return;
    }
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-supervisor-summary', {
        body: {
          clientName:             client ? `${client.first_name} ${client.last_name}` : '',
          clientDob:              client?.date_of_birth || null,
          periodStart:            formData.summary_period_start,
          periodEnd:              formData.summary_period_end,
          assignedNurse:          formData.assigned_nurse_name,
          overallCondition:       formData.overall_condition,
          vitalSignsStatus:       formData.vital_signs_status,
          skinIntegrity:          formData.skin_integrity,
          clinicalNotes:          formData.clinical_notes,
          medicationTechnique:    formData.medication_technique,
          performanceRating:      formData.performance_rating,
          strengths:              formData.strengths,
          improvementsNeeded:     formData.improvements_needed,
          competencyFollowup:     formData.competency_followup,
          satisfactionOverall:    formData.satisfaction_overall,
          satisfactionConsistency: formData.satisfaction_consistency,
          satisfactionCommunication: formData.satisfaction_communication,
          familyConcerns:         formData.family_concerns,
        },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      const text = data?.summary || '';
      setFormData(prev => ({ ...prev, ai_summary_draft: text, final_summary: text }));
      showToast('AI summary generated — review and edit before sending.');
    } catch (e) {
      showToast(`Generation failed: ${e.message}`, 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Complete visit
  const completeVisit = async () => {
    if (loadError) { showToast('Reload the page before completing — the saved visit did not load.', 'error'); return; }
    if (!attested) {
      showToast('Please check the attestation box before completing.', 'error');
      return;
    }
    if (summaryDue && !formData.final_summary?.trim()) {
      showToast('A physician summary is due (60-90 day HFRD cadence) — please generate and review it before completing.', 'error');
      return;
    }
    if (!formData.supervisor_name || !formData.visit_date || !formData.visit_type) {
      showToast('Visit details (Tab 1) are incomplete.', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload('complete');
      const cName   = payload.client_name;
      const cId     = payload.client_id;

      // 1. Save/update visit as complete
      let svId = visitId;
      if (svId) {
        const { error } = await supabase.from('supervisor_visits').update(payload).eq('id', svId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('supervisor_visits').insert([payload]).select('id').single();
        if (error) throw error;
        svId = data.id;
      }

      clearBackup(); // visit is saved — drop the device backup

      // 1.5 Create version record for audit trail
      if (svId && profile?.id) {
        try {
          await createSupervisorVisitVersion(svId, payload, profile.id);
        } catch (versionErr) {
          console.warn('Version creation failed:', versionErr);
          // Don't throw — versioning is secondary to main save
        }
      }

      // 2. Insert physician_summaries log — only when a summary was actually
      // due or the reviewer chose to send one anyway (voluntary send).
      const summarySent = summaryDue || !!formData.final_summary?.trim();
      if (summarySent) {
        await supabase.from('physician_summaries').insert([{
          client_id:          cId,
          client_ref:         payload.client_ref,
          client_name:        cName,
          supervisor_visit_id: svId,
          physician_name:     formData.physician_name || 'Physician',
          delivery_method:    formData.physician_delivery_method || 'fax',
          fax_email:          formData.physician_fax_email || '',
          period_start:       formData.summary_period_start || formData.visit_date,
          period_end:         formData.summary_period_end   || formData.visit_date,
          summary_text:       formData.final_summary,
          recommended_actions: formData.recommended_physician_actions || '',
          sent_by:            profile?.full_name || 'Staff',
        }]);
      }

      // 3. Archive any other open (non-complete) drafts of the SAME visit
      // type for this client. Scoped per-type, not client-wide — GAPP
      // clients run two independent concurrent tracks (biweekly PSS +
      // monthly RN assessment), so a client-wide archive would wipe out
      // the other track's pending draft.
      if (cId && formData.visit_type) {
        await supabase.from('supervisor_visits')
          .update({ status: 'archived' })
          .eq('client_id', cId)
          .eq('visit_type', formData.visit_type)
          .neq('status', 'complete')
          .neq('id', svId);
      }

      // 4. Create the next draft(s), scheduled from TODAY (not the visit
      // date — a late completion shouldn't also delay the future schedule).
      // initial_visit seeds the client's regular cadence track(s);
      // gapp_pss_biweekly/gapp_rn_monthly/scheduled each advance their own
      // track; unannounced/follow_up are supplementary and don't spawn one.
      const recurringTypes = ['gapp_pss_biweekly', 'gapp_rn_monthly', 'scheduled'];
      const nextVisitTypes = formData.visit_type === 'initial_visit'
        ? (client?.service_type === 'gapp' ? ['gapp_pss_biweekly', 'gapp_rn_monthly'] : ['scheduled'])
        : recurringTypes.includes(formData.visit_type) ? [formData.visit_type] : [];

      for (const nt of nextVisitTypes) {
        const nextDate = nextDueDateFor(nt, new Date());
        await supabase.from('supervisor_visits').insert([{
          client_id:    cId,
          client_ref:   payload.client_ref,
          client_name:  cName,
          supervisor_name: 'Pending assignment',
          visit_date:   nextDate,
          visit_type:   nt,
          status:       'draft',
          next_visit_due: nextDate,
        }]);
      }

      // Completed supervisor visits are reported in the daily digest ("Activities
      // Completed Yesterday"), sourced from supervisor_visits.completed_at — no
      // real-time email is sent here.

      showToast('Visit completed — generating report...');

      // Generate and download the DOCX report
      try {
        await downloadSupervisorVisitDoc({ visit: { ...payload, id: svId }, client });
      } catch (docErr) {
        console.warn('DOCX generation failed:', docErr);
        // Don't block navigation — report can be re-downloaded from the list
      }

      // Also upload the report to Drive and link it on the client's Documents page
      // (best-effort — never blocks completion).
      try {
        const blob = await generateSupervisorVisitDoc({ visit: { ...payload, id: svId }, client });
        const filename = `SupervisoryVisit_${(cName || 'Client').replace(/\s+/g, '_')}_${payload.visit_date}.docx`;
        await uploadAndLinkDoc({
          blob, filename, clientCode: payload.client_ref, clientUuid: cId,
          documentType: 'Supervisor Visit', subfolder: 'Supervisor Visits',
          notes: `Supervisory visit — ${payload.visit_date}`, profile,
        });
      } catch (upErr) {
        console.warn('Supervisor visit Drive link failed:', upErr);
      }

      showToast('Visit completed and report downloaded.');
      setTimeout(() => navigate('/hh/supervisor-visits'), 1800);
    } catch (e) {
      showToast(`Error completing visit: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleCompetency = (item) => {
    setFormData(prev => ({
      ...prev,
      competency_followup: prev.competency_followup.includes(item)
        ? prev.competency_followup.filter(c => c !== item)
        : [...prev.competency_followup, item],
    }));
  };

  const fmtVisitDate = formData.visit_date
    ? (() => { try { return format(parseISO(formData.visit_date), 'MMMM d, yyyy'); } catch { return formData.visit_date; } })()
    : '[visit date]';
  const nextDueDate = formData.visit_date
    ? (() => { try { return nextDueDateFor(formData.visit_type, parseISO(formData.visit_date)); } catch { return null; } })()
    : null;
  const nextDueStr = nextDueDate
    ? (() => { try { return format(parseISO(nextDueDate), 'MMMM d, yyyy'); } catch { return ''; } })()
    : '';

  // ── Render tabs ──────────────────────────────────────────────

  const renderTab = () => {
    switch (activeTab) {
      case 0: return (
        <>
          <SectionCard title="Visit information">
            <FieldRow>
              <Field label="Supervisor name" required half>
                <FSelect value={formData.supervisor_name}
                  onChange={v => {
                    const sup = supervisors.find(s => s.full_name === v);
                    setFormData(prev => ({ ...prev, supervisor_name: v, supervisor_id: sup?.id || '' }));
                  }}
                  options={supervisors.map(s => ({ value: s.full_name, label: s.full_name }))} />
              </Field>
              <Field label="Visit date" required half>
                <FInput type="date" value={formData.visit_date} onChange={set('visit_date')} />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Visit type" required half>
                <FSelect value={formData.visit_type} onChange={set('visit_type')}
                  options={client?.service_type === 'gapp' ? VISIT_TYPE_OPTIONS_GAPP : VISIT_TYPE_OPTIONS_NON_GAPP} />
              </Field>
              <Field label="Visit duration (minutes)" half>
                <FInput type="number" value={formData.visit_duration_minutes} onChange={set('visit_duration_minutes')} placeholder="e.g. 45" />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Nurse present during visit?">
                <div style={{ display: 'flex', gap: 20, marginTop: 4 }}>
                  {['Yes', 'No'].map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input type="radio" checked={formData.nurse_present === (opt === 'Yes')}
                        onChange={() => set('nurse_present')(opt === 'Yes')}
                        style={{ accentColor: STEEL }} /> {opt}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Assigned nurse name" half>
                <FInput value={formData.assigned_nurse_name} onChange={set('assigned_nurse_name')} placeholder="Nurse's full name" />
              </Field>
            </FieldRow>

            {formData.visit_type === 'gapp_pss_biweekly' && (
              <FieldRow>
                <Field label="Performed by LPN on RN's behalf?">
                  <div style={{ display: 'flex', gap: 20, marginTop: 4 }}>
                    {['Yes', 'No'].map(opt => (
                      <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                        <input type="radio" checked={formData.lpn_performed_on_rn_behalf === (opt === 'Yes')}
                          onChange={() => set('lpn_performed_on_rn_behalf')(opt === 'Yes')}
                          style={{ accentColor: STEEL }} /> {opt}
                      </label>
                    ))}
                  </div>
                </Field>
                {formData.lpn_performed_on_rn_behalf && (
                  <Field label="RN Supervisor (countersigner)" half>
                    <FSelect value={formData.rn_countersigner_name} onChange={set('rn_countersigner_name')}
                      options={supervisors.map(s => ({ value: s.full_name, label: s.full_name }))} />
                  </Field>
                )}
              </FieldRow>
            )}
            {formData.visit_type === 'gapp_pss_biweekly' && formData.lpn_performed_on_rn_behalf && (
              <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: -8 }}>
                RN must countersign within 10 business days
                {formData.visit_date ? ` — due ${(() => { try { return format(addBusinessDays(parseISO(formData.visit_date), 10), 'MMM d, yyyy'); } catch { return ''; } })()}` : ''}
                {' '}(GAPP Provider Manual §901.17.4.2.5).
                {formData.rn_countersigned_at
                  ? ` Countersigned ${format(parseISO(formData.rn_countersigned_at.slice(0, 10)), 'MMM d, yyyy')} by ${formData.rn_countersigned_by}.`
                  : ' Countersign from the Supervisor Visits list once reviewed.'}
              </p>
            )}

            <FieldRow>
              <Field label="This visit was triggered by a sentinel event?">
                <div style={{ display: 'flex', gap: 20, marginTop: 4 }}>
                  {['Yes', 'No'].map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input type="radio" checked={formData.sentinel_event === (opt === 'Yes')}
                        onChange={() => set('sentinel_event')(opt === 'Yes')}
                        style={{ accentColor: STEEL }} /> {opt}
                    </label>
                  ))}
                </div>
              </Field>
              {formData.sentinel_event && (
                <Field label="Date of sentinel event" half>
                  <FInput type="date" value={formData.sentinel_event_date} onChange={set('sentinel_event_date')} />
                </Field>
              )}
            </FieldRow>
            {formData.sentinel_event && formData.sentinel_event_date && formData.visit_date && (() => {
              let gap = null;
              try { gap = differenceInBusinessDays(parseISO(formData.visit_date), parseISO(formData.sentinel_event_date)); } catch { /* noop */ }
              const compliant = gap !== null && gap <= 1;
              return (
                <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 6, marginTop: -8, marginBottom: 4,
                  background: compliant ? 'var(--success-bg)' : 'var(--warn-bg)', color: compliant ? 'var(--success)' : 'var(--warn)' }}>
                  {compliant
                    ? '✓ Within the 24-hour/next-business-day requirement.'
                    : `⚠ Visit occurred ${gap} business days after the sentinel event — exceeds the 24-hour/next-business-day requirement.`}
                </div>
              );
            })()}
          </SectionCard>
        </>
      );

      case 1: return (
        <>
          <SectionCard title="Clinical observations">
            <FieldRow>
              <Field label="Overall condition vs last visit" required half>
                <FSelect value={formData.overall_condition} onChange={set('overall_condition')} options={[
                  { value: 'improved',              label: 'Improved' },
                  { value: 'stable',                label: 'Stable' },
                  { value: 'declined',              label: 'Declined' },
                  { value: 'significantly_declined', label: 'Significantly declined' },
                ]} />
              </Field>
              <Field label="Vital signs reviewed and in range?" required half>
                <FSelect value={formData.vital_signs_status} onChange={set('vital_signs_status')} options={[
                  'Yes — all within normal range',
                  'Yes — some concerns noted',
                  'No — not reviewed',
                ]} />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Skin / wound integrity" half>
                <FSelect value={formData.skin_integrity} onChange={set('skin_integrity')} options={[
                  'Intact — no concerns',
                  'Minor concerns noted',
                  'Wound present — documented',
                  'N/A',
                ]} />
              </Field>
              <Field label="Equipment functioning correctly?" half>
                <FSelect value={formData.equipment_functioning} onChange={set('equipment_functioning')} options={[
                  'Yes',
                  'No — issue documented below',
                  'N/A',
                ]} />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Clinical observations and notes">
                <FTextarea value={formData.clinical_notes} onChange={set('clinical_notes')}
                  placeholder="Document all clinical findings, concerns, and observations…" rows={5} />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Medication administration — correct technique observed?" required half>
                <FSelect value={formData.medication_technique} onChange={set('medication_technique')} options={[
                  'Yes — correct technique observed',
                  'No — technique issue noted',
                  'N/A — no medications administered',
                ]} />
              </Field>
              <Field label="Medication list current and reconciled?" half>
                <FSelect value={formData.medication_list_current} onChange={set('medication_list_current')} options={[
                  'Yes — list current',
                  'No — discrepancies noted',
                  'Not reviewed',
                ]} />
              </Field>
            </FieldRow>
          </SectionCard>
        </>
      );

      case 2: return (
        <>
          <SectionCard title="Performance checklist">
            <ChecklistItem label="Documentation up to date and accurate"
              checked={formData.documentation_current} onChange={set('documentation_current')} />
            <ChecklistItem label="Care plan being followed as written"
              checked={formData.care_plan_followed} onChange={set('care_plan_followed')} />
            <ChecklistItem label="Infection control practices observed"
              checked={formData.infection_control_observed} onChange={set('infection_control_observed')} />
            <ChecklistItem label="Emergency protocols reviewed with family"
              checked={formData.emergency_protocol_reviewed} onChange={set('emergency_protocol_reviewed')} />
            <ChecklistItem label="Family / caregiver interaction professional and appropriate"
              checked={formData.family_interaction_ok} onChange={set('family_interaction_ok')} />
            <ChecklistItem label="Caregiver education and teaching ongoing"
              checked={formData.caregiver_education_ongoing} onChange={set('caregiver_education_ongoing')} />
          </SectionCard>

          <SectionCard title="Overall rating">
            <FieldLabel required>Overall performance rating</FieldLabel>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, marginTop: 6 }}>
              {[
                { value: 'meets_expectations',   label: 'Meets expectations' },
                { value: 'exceeds_expectations', label: 'Exceeds expectations' },
                { value: 'below_expectations',   label: 'Below expectations' },
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                  padding: '8px 14px', borderRadius: 7, border: `1.5px solid ${formData.performance_rating === opt.value ? STEEL : BORDER}`,
                  background: formData.performance_rating === opt.value ? 'var(--accent-bg)' : '#fff', fontSize: 13 }}>
                  <input type="radio" checked={formData.performance_rating === opt.value}
                    onChange={() => set('performance_rating')(opt.value)}
                    style={{ accentColor: STEEL }} /> {opt.label}
                </label>
              ))}
            </div>
            <FieldRow>
              <Field label="Areas of strength">
                <FTextarea value={formData.strengths} onChange={set('strengths')} placeholder="Document specific strengths observed…" />
              </Field>
              <Field label="Areas requiring improvement">
                <FTextarea value={formData.improvements_needed} onChange={set('improvements_needed')} placeholder="Document areas needing improvement…" />
              </Field>
            </FieldRow>
          </SectionCard>

          <SectionCard title="Competency follow-up">
            <FieldLabel>Competencies requiring follow-up training (select all that apply)</FieldLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: 8, marginBottom: 16 }}>
              {COMPETENCIES.map(c => (
                <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                  padding: '6px 12px', borderRadius: 6, border: `1.5px solid ${formData.competency_followup.includes(c) ? STEEL : BORDER}`,
                  background: formData.competency_followup.includes(c) ? 'var(--accent-bg)' : '#fff', fontSize: 12 }}>
                  <input type="checkbox" checked={formData.competency_followup.includes(c)}
                    onChange={() => toggleCompetency(c)} style={{ accentColor: STEEL }} /> {c}
                </label>
              ))}
            </div>
            <FieldRow>
              <Field label="Follow-up visit required?">
                <div style={{ display: 'flex', gap: 20, marginTop: 4 }}>
                  {['Yes', 'No'].map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input type="radio" checked={formData.followup_visit_required === (opt === 'Yes')}
                        onChange={() => set('followup_visit_required')(opt === 'Yes')}
                        style={{ accentColor: STEEL }} /> {opt}
                    </label>
                  ))}
                </div>
              </Field>
              {formData.followup_visit_required && (
                <Field label="Follow-up visit date" half>
                  <FInput type="date" value={formData.followup_visit_date} onChange={set('followup_visit_date')} />
                </Field>
              )}
            </FieldRow>
          </SectionCard>

          <SectionCard title="Corrective action">
            <FieldRow>
              <Field label="Corrective action required?">
                <div style={{ display: 'flex', gap: 20, marginTop: 4 }}>
                  {['Yes', 'No'].map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input type="radio" checked={formData.corrective_action_required === (opt === 'Yes')}
                        onChange={() => set('corrective_action_required')(opt === 'Yes')}
                        style={{ accentColor: STEEL }} /> {opt}
                    </label>
                  ))}
                </div>
              </Field>
              {formData.corrective_action_required && (
                <Field label="Due date (14 days — system-computed)" half>
                  <FInput readOnly value={formData.visit_date
                    ? (() => { try { return format(addDays(parseISO(formData.visit_date), 14), 'MMM d, yyyy'); } catch { return ''; } })()
                    : ''} />
                </Field>
              )}
            </FieldRow>
            {formData.corrective_action_required && (
              <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: -8 }}>
                Corrective action must be addressed within 14 days (Policy A-3.7). Describe it above in "Areas requiring improvement."
                {formData.corrective_action_resolved_at
                  ? ` Resolved ${format(parseISO(formData.corrective_action_resolved_at.slice(0, 10)), 'MMM d, yyyy')} by ${formData.corrective_action_resolved_by}.`
                  : ' Mark resolved from the Supervisor Visits list once addressed.'}
              </p>
            )}
          </SectionCard>
        </>
      );

      case 3: return (
        <>
          <SectionCard title="Family satisfaction ratings">
            <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16, marginTop: 0 }}>
              Click stars to rate each dimension (1 = poor, 5 = excellent)
            </p>
            {[
              { label: 'Overall satisfaction with care',          key: 'satisfaction_overall' },
              { label: 'Nurse consistency and reliability',       key: 'satisfaction_consistency' },
              { label: 'Communication and information sharing',   key: 'satisfaction_communication' },
              { label: 'Family understanding of care plan',       key: 'satisfaction_care_plan' },
            ].map(({ label, key }) => (
              <div key={key} style={{ marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>{label}</div>
                <StarRating value={formData[key]} onChange={set(key)} />
              </div>
            ))}
          </SectionCard>
          <SectionCard title="Family concerns">
            <FieldRow>
              <Field label="Concerns raised by family during visit">
                <FTextarea value={formData.family_concerns} onChange={set('family_concerns')}
                  placeholder="Document any concerns, requests, or feedback raised by family…" rows={5} />
              </Field>
            </FieldRow>
          </SectionCard>
        </>
      );

      case 4: return (
        <>
          {summaryDue ? (
            <div style={{ background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', borderRadius: 10,
              padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <svg width="18" height="18" fill="none" stroke={STEEL} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div style={{ fontSize: 13, color: NAVY }}>
                <strong>Physician summary due</strong> — {lastSummaryDate ? `last summary sent ${format(parseISO(lastSummaryDate.slice(0, 10)), 'MMM d, yyyy')}, ` : 'no summary on file yet, '}
                and the HFRD-required 60-90 day interval has elapsed (Policy A-3.4). The summary is auto-stored in the patient's clinical record.
              </div>
            </div>
          ) : (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 10,
              padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <svg width="18" height="18" fill="none" stroke="var(--text3)" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                <strong>Not due yet</strong> — last physician summary sent {lastSummaryDate ? format(parseISO(lastSummaryDate.slice(0, 10)), 'MMM d, yyyy') : '—'};
                the 60-90 day HFRD cadence (Policy A-3.4) is independent of this visit's own cadence. Optional — you may still send one now if needed.
              </div>
            </div>
          )}

          <SectionCard title="Physician details">
            <FieldRow>
              <Field label="Physician name" required half>
                <FInput value={formData.physician_name} onChange={set('physician_name')} placeholder="Dr. Name" />
              </Field>
              <Field label="Delivery method" required half>
                <FSelect value={formData.physician_delivery_method} onChange={set('physician_delivery_method')} options={[
                  { value: 'fax',          label: 'Fax' },
                  { value: 'secure_email', label: 'Secure email' },
                  { value: 'in_person',    label: 'In person' },
                ]} />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Fax / email address" half>
                <FInput value={formData.physician_fax_email} onChange={set('physician_fax_email')}
                  placeholder="Pre-filled from client record" />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Period start" required half>
                <FInput type="date" value={formData.summary_period_start} onChange={set('summary_period_start')} />
              </Field>
              <Field label="Period end" required half>
                <FInput type="date" value={formData.summary_period_end} onChange={set('summary_period_end')} />
              </Field>
            </FieldRow>
          </SectionCard>

          <SectionCard title="AI-generated summary">
            <div style={{ marginBottom: 16 }}>
              <button onClick={generateSummary} disabled={generating}
                style={{ padding: '9px 20px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                  background: generating ? 'var(--border2)' : STEEL, color: '#fff', border: 'none',
                  cursor: generating ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                {generating ? (
                  <>
                    <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    Generating…
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
                    </svg>
                    Generate AI summary draft
                  </>
                )}
              </button>
            </div>
            <FieldLabel>Physician summary (edit before sending)</FieldLabel>
            <FTextarea value={formData.final_summary || formData.ai_summary_draft}
              onChange={v => setFormData(prev => ({ ...prev, final_summary: v, ai_summary_draft: v }))}
              placeholder="Click 'Generate AI summary draft' above, or type the summary manually…" rows={10} />
            <FieldRow>
              <Field label="Recommended physician actions">
                <FTextarea value={formData.recommended_physician_actions}
                  onChange={set('recommended_physician_actions')}
                  placeholder="Any specific actions requested from the physician…" rows={3} />
              </Field>
            </FieldRow>
          </SectionCard>
        </>
      );

      case 5: return (
        <>
          <SectionCard title="Visit summary">
            <ReviewRow label="Patient"              value={client ? `${client.last_name}, ${client.first_name}` : formData.client_name} />
            <ReviewRow label="Client ID"             value={client?.client_id || formData.client_ref} />
            <ReviewRow label="Supervisor"           value={formData.supervisor_name} />
            <ReviewRow label="Visit date"           value={fmtVisitDate} />
            <ReviewRow label="Visit type"           value={VISIT_TYPE_LABELS[formData.visit_type] || formData.visit_type} />
            <ReviewRow label="Assigned nurse"       value={formData.assigned_nurse_name} />
          </SectionCard>

          <SectionCard title="Clinical">
            <ReviewRow label="Overall condition"      value={formData.overall_condition?.replace(/_/g, ' ')} />
            <ReviewRow label="Vital signs"            value={formData.vital_signs_status} />
            <ReviewRow label="Medication technique"   value={formData.medication_technique} />
            <ReviewRow label="Performance rating"     value={formData.performance_rating?.replace(/_/g, ' ')} />
            {formData.competency_followup?.length > 0 && (
              <ReviewRow label="Competency follow-up" value={formData.competency_followup.join(', ')} />
            )}
          </SectionCard>

          <SectionCard title="Family satisfaction">
            <ReviewRow label="Overall satisfaction"    value={formData.satisfaction_overall ? `${formData.satisfaction_overall}/5` : null} />
            <ReviewRow label="Nurse consistency"       value={formData.satisfaction_consistency ? `${formData.satisfaction_consistency}/5` : null} />
            <ReviewRow label="Communication"           value={formData.satisfaction_communication ? `${formData.satisfaction_communication}/5` : null} />
            <ReviewRow label="Care plan understanding" value={formData.satisfaction_care_plan ? `${formData.satisfaction_care_plan}/5` : null} />
          </SectionCard>

          <SectionCard title="Physician summary">
            <ReviewRow label="Physician"        value={formData.physician_name} />
            <ReviewRow label="Delivery method"  value={formData.physician_delivery_method?.replace(/_/g, ' ')} />
            <ReviewRow label="Fax / email"      value={formData.physician_fax_email} />
            <ReviewRow label="Period"           value={formData.summary_period_start && formData.summary_period_end
              ? `${formData.summary_period_start} → ${formData.summary_period_end}` : null} />
            {formData.final_summary && (
              <div style={{ marginTop: 10, padding: 14, background: LIGHT, borderRadius: 7,
                fontSize: 13, color: 'var(--text)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                {formData.final_summary}
              </div>
            )}
          </SectionCard>

          {/* Attestation */}
          <div style={{ background: attested ? 'var(--success-bg)' : 'var(--warn-bg)', border: `1.5px solid ${attested ? 'var(--success-border)' : 'var(--warn-border)'}`,
            borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={attested} onChange={e => setAttested(e.target.checked)}
                style={{ marginTop: 3, accentColor: STEEL, width: 18, height: 18, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>
                I, <strong>{formData.supervisor_name || '[supervisor name]'}</strong>, attest that I conducted this supervisory visit
                in person in the client's home on <strong>{fmtVisitDate}</strong>, that all findings are accurate{summaryDue
                  ? ', and that the physician summary has been reviewed and is ready to send.'
                  : '.'}
              </span>
            </label>
          </div>

          {/* Next visit note */}
          {nextDueStr && (
            <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info-border)', borderRadius: 8,
              padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--info)' }}>
              📅 Next supervisor visit will be automatically scheduled for <strong>{nextDueStr}</strong>
            </div>
          )}
        </>
      );

      default: return null;
    }
  };

  if (loadError) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 16, padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 40 }}>⚠️</div>
      <div style={{ color: 'var(--danger)', fontSize: 15, fontWeight: 700, maxWidth: 460 }}>{loadError}</div>
      <button onClick={() => window.location.reload()} style={{ padding: '10px 22px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
        Reload page
      </button>
    </div>
  );

  return (
    <div style={{ padding: '0 24px 20px', maxWidth: 900, margin: '0 auto' }}>
      {client?.id
        ? <ClientTrail client={client} crumbs={[...tabCrumbs(client.id, 'clinical', 'supervision'), { label: isNew ? 'New supervisory visit' : 'Supervisory visit' }]} />
        : <ClientTrail root={[{ label: 'Supervisory Visits', to: '/hh/supervisor-visits' }]} crumbs={[{ label: isNew ? 'New supervisory visit' : 'Supervisory visit' }]} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate(client?.id ? clientTabPath(client.id, 'clinical', 'supervision') : '/hh/supervisor-visits')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 4 }}>
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
            {isNew ? 'New supervisor visit' : 'Supervisor visit'}
          </h2>
          {client && (
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text3)' }}>
              {client.last_name}, {client.first_name} · {client.client_id}
            </p>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 2, overflowX: 'auto', marginBottom: 0,
        borderBottom: `2px solid ${BORDER}`, paddingBottom: 0 }}>
        {TABS.map((t, i) => (
          <TabButton key={t} label={t} index={i} active={activeTab === i} onClick={setActiveTab} />
        ))}
      </div>

      {/* Tab content */}
      <div style={{ marginTop: 24 }}>
        {loadedStatus === 'complete' && (
          <div style={{ background: 'var(--warn-bg)', border: '1.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 8 }}>
            ⚠ This visit is marked complete. Editing will return it to Draft status.
          </div>
        )}
        {renderTab()}
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap',
        borderTop: `1px solid ${BORDER}`, paddingTop: 20, marginTop: 8 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {activeTab > 0 && (
            <button onClick={() => setActiveTab(t => t - 1)}
              style={{ padding: '9px 18px', borderRadius: 7, fontSize: 13, fontWeight: 500,
                background: 'var(--card-bg)', color: 'var(--text2)', border: '1px solid var(--border)', cursor: 'pointer' }}>
              ← Back
            </button>
          )}
          {activeTab < TABS.length - 1 && (
            <button onClick={() => setActiveTab(t => t + 1)}
              style={{ padding: '9px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                background: NAVY, color: '#fff', border: 'none', cursor: 'pointer' }}>
              Next →
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={saveDraft} disabled={saving}
            style={{ padding: '9px 18px', borderRadius: 7, fontSize: 13, fontWeight: 500,
              background: 'var(--card-bg)', color: 'var(--text)', border: '1px solid var(--border)',
              cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          {activeTab === 5 && (
            <button onClick={completeVisit} disabled={saving || !attested}
              style={{ padding: '9px 22px', borderRadius: 7, fontSize: 13, fontWeight: 700,
                background: attested ? 'var(--success)' : 'var(--border2)', color: '#fff', border: 'none',
                cursor: attested && !saving ? 'pointer' : 'not-allowed' }}>
              {saving ? 'Completing…' : 'Send summary & complete visit'}
            </button>
          )}
        </div>
      </div>

      <Toast {...(toast || {})} onClose={hideToast} />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
